import React, { useState, useEffect, useRef } from 'react';
import * as flatpickrModule from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
// flatpickr module may export default or as a namespace
const flatpickr = (flatpickrModule as unknown as { default?: typeof flatpickrModule }).default ?? flatpickrModule;
import type { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import BookingManagementProtectedRoute from '../admin/BookingManagementProtectedRoute';
import { checkAvailability, submitBookingRequest, fetchProperty, fetchAvailableDates } from '../../utils/api';
import { calculatePrice, formatPrice, calculateNights } from '../../utils/dateUtils';
import { collection, query, getDocs, updateDoc, doc, orderBy, setDoc, deleteDoc } from 'firebase/firestore';
import { Check, Copy } from 'lucide-react';
import { auth, db } from '../../firebase';
import type { Property, Booking } from '../../types';

interface BookingEngineProps {
  isAdminView?: boolean;
}

const PROPERTY_ID = 'villa_kourtsoudi_01';
const HOSTHUB_CALENDAR_FEED_URL = 'https://rizessifnos.netlify.app/.netlify/functions/calendar-feed';

const getPaymentResultFromLocation = (): 'paid' | 'cancelled' | null => {
  const hashQuery = window.location.hash.split('?')[1] || '';
  const params = new URLSearchParams(window.location.search || hashQuery);
  if (params.get('paid') === '1') return 'paid';
  if (params.get('cancelled') === '1') return 'cancelled';
  return null;
};

const parseDateInput = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  date.setHours(0, 0, 0, 0);
  return date;
};

const BookingEngine: React.FC<BookingEngineProps> = ({ isAdminView = false }) => {
  // ---------- PROPERTY (loaded from Firestore) ----------
  const [property, setProperty] = useState<Property | null>(null);
  const [propertyLoading, setPropertyLoading] = useState(true);

  // ---------- ADMIN BANNER ----------
  const [emailBanner, setEmailBanner] = useState<{ type: 'success' | 'warning'; message: string } | null>(null);

  // ---------- GUEST FORM STATE ----------
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [checkin, setCheckin] = useState('');
  const [checkout, setCheckout] = useState('');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [paymentResult] = useState<'paid' | 'cancelled' | null>(getPaymentResultFromLocation);

  // ---------- ADMIN STATE ----------
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    booking: Booking;
    status: 'APPROVED' | 'REJECTED' | 'CANCELLED';
  } | null>(null);
  const [calendarUrlCopied, setCalendarUrlCopied] = useState(false);

  // ---------- FLATPICKR REFS ----------
  const checkinInputRef = useRef<HTMLInputElement | null>(null);
  const checkoutInputRef = useRef<HTMLInputElement | null>(null);
  const checkinPickerRef = useRef<FlatpickrInstance | null>(null);
  const checkoutPickerRef = useRef<FlatpickrInstance | null>(null);

  useEffect(() => {
    let isMounted = true;
    fetchProperty(PROPERTY_ID).then((p) => {
      if (!isMounted) return;
      setProperty(p);
      setPropertyLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (propertyLoading) return;
    let isMounted = true;
    const initializeDatePickers = () => {
      if (checkinInputRef.current) {
        checkinPickerRef.current = flatpickr(checkinInputRef.current, {
          dateFormat: 'Y-m-d',
          minDate: 'today',
          clickOpens: true,
          disable: [],
          onChange: (selectedDates, dateStr) => {
            setCheckin(dateStr);
            if (checkoutPickerRef.current) {
              if (selectedDates[0]) {
                const nextDay = new Date(selectedDates[0]);
                nextDay.setDate(nextDay.getDate() + 1);
                checkoutPickerRef.current.set('minDate', nextDay);
              } else {
                checkoutPickerRef.current.set('minDate', 'today');
              }
            }
          },
        });
      }

      if (checkoutInputRef.current) {
        checkoutPickerRef.current = flatpickr(checkoutInputRef.current, {
          dateFormat: 'Y-m-d',
          minDate: 'today',
          clickOpens: true,
          disable: [],
          onChange: (_selectedDates, dateStr) => {
            setCheckout(dateStr);
          },
        });
      }
    };

    initializeDatePickers();

    fetchAvailableDates(PROPERTY_ID).then((dates) => {
      if (!isMounted) return;
      const disabledRanges = dates.map((range) => {
        const start = range.start || range.date;
        const end = range.end || range.date;
        const [year, month, day] = end.split('-').map(Number);
        const toDate = new Date(year, month - 1, day);
        toDate.setDate(toDate.getDate() - 1);
        const toStr = [
          toDate.getFullYear(),
          String(toDate.getMonth() + 1).padStart(2, '0'),
          String(toDate.getDate()).padStart(2, '0'),
        ].join('-');

        if (toStr >= start) {
          return { from: start, to: toStr };
        }
        return { from: start, to: start };
      });
      checkinPickerRef.current?.set('disable', disabledRanges);
      checkoutPickerRef.current?.set('disable', disabledRanges);
    }).catch((error) => {
      console.error('Could not load blocked dates for the calendar:', error);
    });

    return () => {
      isMounted = false;
      checkinPickerRef.current?.destroy();
      checkoutPickerRef.current?.destroy();
    };
  }, [propertyLoading]);

  useEffect(() => {
    if (!checkin && checkinPickerRef.current) checkinPickerRef.current.clear();
  }, [checkin]);

  useEffect(() => {
    if (!checkout && checkoutPickerRef.current) checkoutPickerRef.current.clear();
  }, [checkout]);

  useEffect(() => {
    if (paymentResult) {
      document.getElementById('book')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [paymentResult]);


  const loadBookings = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'bookings'), orderBy('created_at', 'desc'));
      const snap = await getDocs(q);
      setBookings(snap.docs.map((d) => d.data() as Booking));
    } catch (err) {
      console.error('Error loading bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdminView) loadBookings();
  }, [isAdminView]);

  // ---------- ADMIN ACTIONS ----------
  const setBookingStatus = async (
    bookingId: string,
    status: 'APPROVED' | 'REJECTED' | 'CANCELLED'
  ) => {
    const booking = bookings.find((b) => b.id === bookingId);
    await updateDoc(doc(db, 'bookings', bookingId), { status });

    // Mirror APPROVED bookings into the public blocked-ranges list
    if (booking) {
      if (status === 'APPROVED') {
        await setDoc(doc(db, 'blockedRanges', bookingId), {
          property_id: booking.property_id,
          start: booking.checkin,
          end: booking.checkout,
        });
      } else {
        await deleteDoc(doc(db, 'blockedRanges', bookingId));
      }
    }

    const shouldRefund = Boolean(
      booking?.payment_id && (status === 'CANCELLED' || status === 'REJECTED')
    );
    let refundSucceeded = false;
    if (shouldRefund && booking?.payment_id) {
      try {
        const token = await auth.currentUser?.getIdToken();
        if (!token) throw new Error('Admin authentication is required to refund this payment.');

        const refundResponse = await fetch('/.netlify/functions/refund-payment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ paymentId: booking.payment_id }),
        });
        const refundData = await refundResponse.json();
        refundSucceeded = refundResponse.ok && refundData.ok === true;
        if (!refundSucceeded) {
          console.error('Refund failed:', refundData.error || `HTTP ${refundResponse.status}`);
        }
      } catch (error) {
        console.error('Refund request failed:', error);
      }
    }

    await loadBookings();

    if (booking) {
      let guestEmailed = false;
      let emailFailure = '';
      try {
        const res = await fetch('/.netlify/functions/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: booking.guest_email,
            guestName: booking.guest_name,
            checkin: booking.checkin,
            checkout: booking.checkout,
            total: booking.total_price,
            nights: booking.nights,
            newStatus: status,
          }),
        });

        if (res.ok) {
          guestEmailed = true;
        } else {
          let reason = `HTTP ${res.status}`;
          try {
            const errData = await res.json();
            if (errData && (errData.error || errData.message)) {
              reason = errData.error || errData.message;
            }
          } catch (_) {
            // non-JSON or other error
          }
          emailFailure = reason;
        }
      } catch (err: unknown) {
        console.error('Failed to send status update email:', err);
        emailFailure = (err as Error)?.message || 'Network error';
      }

      if (shouldRefund && !refundSucceeded) {
        setEmailBanner({
          type: 'warning',
          message: `Dates freed but refund FAILED — do it manually in Stripe dashboard.${emailFailure ? ` Guest email also failed: ${emailFailure}` : guestEmailed ? ' Guest emailed.' : ''}`,
        });
      } else if (emailFailure) {
        setEmailBanner({
          type: 'warning',
          message: `Status updated but email FAILED: ${emailFailure}${refundSucceeded ? ' Payment refunded.' : ''}`,
        });
      } else if (refundSucceeded) {
        setEmailBanner({
          type: 'success',
          message: 'Status updated — guest emailed, payment refunded ✉️💶',
        });
      } else if (guestEmailed) {
        setEmailBanner({
          type: 'success',
          message: 'Status updated — guest emailed ✉️',
        });
      }
    }
  };

  const copyCalendarFeedUrl = async () => {
    try {
      await navigator.clipboard.writeText(HOSTHUB_CALENDAR_FEED_URL);
      setCalendarUrlCopied(true);
      window.setTimeout(() => setCalendarUrlCopied(false), 2000);
    } catch (error) {
      console.error('Could not copy calendar feed URL:', error);
    }
  };

  const confirmStatusChange = async () => {
    if (!pendingStatusChange) return;
    await setBookingStatus(pendingStatusChange.booking.id, pendingStatusChange.status);
    setPendingStatusChange(null);
  };

  // ---------- GUEST SUBMIT ----------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!property) {
      setError('Loading property info, please try again in a second.');
      return;
    }
    if (!guestName || !guestEmail || !checkin || !checkout) {
      setError('Please fill in all fields.');
      return;
    }

    const checkinDate = parseDateInput(checkin);
    const checkoutDate = parseDateInput(checkout);
    if (!checkinDate || !checkoutDate) {
      setError('Please choose valid check-in and check-out dates.');
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (checkinDate < today || checkoutDate < today) {
      setError('Past dates are not allowed. Please choose today or a future date.');
      return;
    }

    if (checkout <= checkin) {
      setError('Check-out must be after check-in.');
      return;
    }

    // Rule 1: minimum nights
    const minNights = property.min_nights ?? 3;
    const nights = calculateNights(checkin, checkout);
    if (nights < minNights) {
      setError(`Minimum stay is ${minNights} nights. You selected ${nights}.`);
      return;
    }

    // Rule 2: maximum guests
    const maxGuests = property.max_guests ?? 4;
    if (adults + children > maxGuests) {
      setError(`Maximum ${maxGuests} guests allowed (you selected ${adults + children}).`);
      return;
    }
    if (adults < 1) {
      setError('At least 1 adult is required.');
      return;
    }

    setIsSubmitting(true);
    try {
      const available = await checkAvailability(PROPERTY_ID, checkin, checkout);
      if (!available) {
        setError('Sorry, those dates are already taken. Please pick another range.');
        return;
      }

      const breakdown = calculatePrice(property, checkin, checkout);

      const result = await submitBookingRequest({
        property_id: PROPERTY_ID,
        checkin,
        checkout,
        adults,
        children,
        guest_name: guestName,
        guest_email: guestEmail,
        total_price: breakdown.total,
        nights,
      });

      if (!result.success || !result.data) {
        setError(result.error || 'Something went wrong. Please try again.');
        return;
      }

      const checkoutResponse = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bookingId: result.data.bookingId,
          propertyId: PROPERTY_ID,
          guestEmail,
          guestName,
          checkin,
          checkout,
          nights,
        }),
      });
      const checkoutData = await checkoutResponse.json();
      if (!checkoutResponse.ok || !checkoutData.url) {
        throw new Error(checkoutData.error || 'Could not start payment.');
      }

      window.location.href = checkoutData.url;
    } catch (err) {
      console.error(err);
      setError('Your booking request was saved, but payment could not be started. Please contact us before submitting again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ================= ADMIN VIEW =================
  if (isAdminView) {
    return (
      <BookingManagementProtectedRoute>
        <div className="container mx-auto px-4 py-8">
          <h2 className="text-2xl font-bold mb-6">Booking Management</h2>

          <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4" aria-label="Calendar sync">
            <h3 className="text-lg font-semibold text-gray-900">HostHub calendar feed</h3>
            <p className="mt-1 text-sm text-gray-600">
              Copy this URL into HostHub to share blocked dates. iCal does not sync prices, guest details, or booking decisions.
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input
                aria-label="Calendar feed URL"
                className="min-w-0 flex-1 rounded border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                readOnly
                value={HOSTHUB_CALENDAR_FEED_URL}
              />
              <button
                type="button"
                onClick={copyCalendarFeedUrl}
                className="inline-flex items-center justify-center gap-2 rounded bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
              >
                {calendarUrlCopied ? <Check size={16} /> : <Copy size={16} />}
                {calendarUrlCopied ? 'Copied' : 'Copy URL'}
              </button>
            </div>
          </section>

          {loading ? (
            <div className="text-center py-8">Loading bookings...</div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No bookings yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr>
                    <th className="py-2 px-4 border-b">Guest</th>
                    <th className="py-2 px-4 border-b">Email</th>
                    <th className="py-2 px-4 border-b">Check-In</th>
                    <th className="py-2 px-4 border-b">Check-Out</th>
                    <th className="py-2 px-4 border-b">Guests</th>
                    <th className="py-2 px-4 border-b">Total</th>
                    <th className="py-2 px-4 border-b">Status</th>
                    <th className="py-2 px-4 border-b">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id}>
                      <td className="py-2 px-4 border-b">{b.guest_name}</td>
                      <td className="py-2 px-4 border-b">{b.guest_email}</td>
                      <td className="py-2 px-4 border-b">{b.checkin}</td>
                      <td className="py-2 px-4 border-b">{b.checkout}</td>
                      <td className="py-2 px-4 border-b">
                        {b.adults ?? '-'} adults{(b.children ?? 0) > 0 ? `, ${b.children} children` : ''}
                      </td>
                      <td className="py-2 px-4 border-b">{formatPrice(b.total_price)}</td>
                      <td className="py-2 px-4 border-b">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          b.status === 'APPROVED' ? 'bg-green-200 text-green-800' :
                          b.status === 'PENDING' ? 'bg-yellow-200 text-yellow-800' :
                          'bg-red-200 text-red-800'
                        }`}>
                          {b.status}
                        </span>
                      </td>
                      <td className="py-2 px-4 border-b">
                        {b.status === 'PENDING' && (
                          <>
                            <button
                              onClick={() => setPendingStatusChange({ booking: b, status: 'APPROVED' })}
                              className="text-green-600 hover:text-green-800 mr-2"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => setPendingStatusChange({ booking: b, status: 'REJECTED' })}
                              className="text-red-600 hover:text-red-800"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {b.status === 'REJECTED' && (
                          <button
                            onClick={() => setPendingStatusChange({ booking: b, status: 'APPROVED' })}
                            className="text-green-600 hover:text-green-800"
                          >
                            Approve
                          </button>
                        )}
                        {b.status === 'APPROVED' && (
                          <button
                            onClick={() => setPendingStatusChange({ booking: b, status: 'CANCELLED' })}
                            className="text-red-600 hover:text-red-800"
                          >
                            Cancel
                          </button>
                        )}
                        {b.status === 'CANCELLED' && (
                          <button
                            onClick={() => setPendingStatusChange({ booking: b, status: 'APPROVED' })}
                            className="text-green-600 hover:text-green-800"
                          >
                            Approve
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {emailBanner && (
            <div className={`mt-6 p-4 rounded-lg flex items-center justify-between ${
              emailBanner.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-yellow-50 border border-yellow-200 text-yellow-800'
            }`}>
              <span>{emailBanner.message}</span>
              <button
                onClick={() => setEmailBanner(null)}
                className="text-gray-500 hover:text-gray-700 font-bold px-2"
              >
                ✕
              </button>
            </div>
          )}

          {pendingStatusChange && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="booking-action-title"
                className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
              >
                <h3 id="booking-action-title" className="text-lg font-semibold text-gray-900">
                  Confirm {pendingStatusChange.status.toLowerCase()} booking
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  {pendingStatusChange.booking.guest_name} · {pendingStatusChange.booking.checkin} to {pendingStatusChange.booking.checkout}
                </p>
                <p className="mt-3 text-sm text-gray-700">
                  This will update the booking and email the guest. Continue?
                </p>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setPendingStatusChange(null)}
                    className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Go back
                  </button>
                  <button
                    type="button"
                    onClick={confirmStatusChange}
                    className={`rounded px-4 py-2 text-sm font-medium text-white ${
                      pendingStatusChange.status === 'APPROVED'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    Confirm {pendingStatusChange.status.toLowerCase()}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </BookingManagementProtectedRoute>
    );
  }

  // ================= GUEST VIEW =================
  const maxGuests = property?.max_guests ?? 4;
  const minNights = property?.min_nights ?? 3;
  const nights = checkin && checkout && checkout > checkin
    ? calculateNights(checkin, checkout)
    : 0;
  const tooFewNights = nights > 0 && nights < minNights;
  const tooManyGuests = adults + children > maxGuests;

  return (
    <section id="book" className="bg-rizes-ivory py-14 sm:py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto lg:max-w-none lg:grid lg:grid-cols-2 lg:gap-12 lg:items-start">
          {/* Left Column - Booking Introduction */}
          <div className="mb-8 lg:mb-0">
            <p className="mb-2 text-xs font-semibold uppercase text-rizes-olive">Direct Booking</p>
            <h2 id="booking-title" className="section-title">Book Your Stay</h2>
            <p className="section-subtitle mx-0">
              A quiet house in Rizes, Sifnos. Two bedrooms, close to Apollonia.
            </p>
            
            {/* Property Details */}
            <div className="mt-8">
              <h3 className="text-sm font-medium text-rizes-olive mb-4">Property Details</h3>
              <dl className="space-y-3 border-t border-rizes-limestone pt-4">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Capacity</dt>
                  <dd className="font-medium text-rizes-ink">{maxGuests} guests</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Minimum stay</dt>
                  <dd className="font-medium text-rizes-ink">{minNights} nights</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Right Column - Booking Form */}
          <div className="rounded-2xl bg-rizes-ivory p-6 sm:p-8 ring-1 ring-rizes-limestone">
            {propertyLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rizes-olive border-t-transparent"></div>
              </div>
            ) : paymentResult ? (
              <div className={`max-w-md mx-auto rounded-lg border p-6 text-center ${
                paymentResult === 'paid'
                  ? 'border-green-200 bg-green-50 text-green-800'
                  : 'border-yellow-200 bg-yellow-50 text-yellow-900'
              }`}>
                <h3 className="text-lg font-semibold">
                  {paymentResult === 'paid'
                    ? 'Payment received — booking confirmed!'
                    : 'Payment cancelled — your request is saved, contact us to complete'}
                </h3>
              </div>
            ) : (
              <form className="w-full" aria-labelledby="booking-title" onSubmit={handleSubmit}>
                {/* Guest Information */}
                <div className="space-y-1">
                  <h4 className="text-xs font-semibold uppercase text-rizes-olive mb-4">Guest Information</h4>
                  
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Full Name
                      </label>
                      <input
                        className="block w-full rounded-md border-0 py-3 px-4 bg-white text-gray-900 ring-1 ring-inset ring-rizes-limestone placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-rizes-olive focus:outline-none sm:text-sm sm:leading-6 transition-colors motion-reduce:transition-none"
                        id="name"
                        type="text"
                        placeholder="Your full name"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <input
                        className="block w-full rounded-md border-0 py-3 px-4 bg-white text-gray-900 ring-1 ring-inset ring-rizes-limestone placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-rizes-olive focus:outline-none sm:text-sm sm:leading-6 transition-colors motion-reduce:transition-none"
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div className="mt-6 space-y-1">
                  <h4 className="text-xs font-semibold uppercase text-rizes-olive mb-4">Stay Dates</h4>
                  
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <label htmlFor="check-in" className="block text-sm font-medium text-gray-700">
                        Check-In
                      </label>
                      <input
                        ref={checkinInputRef}
                        className="block w-full rounded-md border-0 py-3 px-4 bg-white text-gray-900 ring-1 ring-inset ring-rizes-limestone placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-rizes-olive focus:outline-none sm:text-sm sm:leading-6 transition-colors motion-reduce:transition-none"
                        id="check-in"
                        type="text"
                        placeholder="YYYY-MM-DD"
                        value={checkin}
                        onChange={(e) => setCheckin(e.target.value)}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label htmlFor="check-out" className="block text-sm font-medium text-gray-700">
                        Check-Out
                      </label>
                      <input
                        ref={checkoutInputRef}
                        className="block w-full rounded-md border-0 py-3 px-4 bg-white text-gray-900 ring-1 ring-inset ring-rizes-limestone placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-rizes-olive focus:outline-none sm:text-sm sm:leading-6 transition-colors motion-reduce:transition-none"
                        id="check-out"
                        type="text"
                        placeholder="YYYY-MM-DD"
                        value={checkout}
                        onChange={(e) => setCheckout(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Nights display */}
                {nights > 0 && (
                  <div className={`mt-4 p-3 rounded-lg text-sm flex items-center justify-between ${
                    tooFewNights ? 'bg-red-50 text-red-700 ring-1 ring-red-200' : 'bg-rizes-ivory text-rizes-olive ring-1 ring-rizes-limestone/50'
                  }`}>
                    <span>
                      {nights} night{nights !== 1 ? 's' : ''} selected
                    </span>
                    {tooFewNights && (
                      <span className="font-medium">Minimum {minNights} nights required</span>
                    )}
                  </div>
                )}

                {/* Guests */}
                <div className="mt-6 space-y-1">
                  <h4 className="text-xs font-semibold uppercase text-rizes-olive mb-4">Guests</h4>
                  
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <label htmlFor="adults" className="block text-sm font-medium text-gray-700">
                        Adults
                      </label>
                      <div className="relative">
                        <input
                          className="block w-full rounded-md border-0 py-3 px-4 bg-white text-gray-900 ring-1 ring-inset ring-rizes-limestone placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-rizes-olive focus:outline-none sm:text-sm sm:leading-6 transition-colors motion-reduce:transition-none [appearance:textfield]"
                          id="adults"
                          type="number"
                          min={1}
                          max={maxGuests}
                          value={adults}
                          onChange={(e) => setAdults(Math.max(1, parseInt(e.target.value) || 1))}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label htmlFor="children" className="block text-sm font-medium text-gray-700">
                        Children
                      </label>
                      <div className="relative">
                        <input
                          className="block w-full rounded-md border-0 py-3 px-4 bg-white text-gray-900 ring-1 ring-inset ring-rizes-limestone placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-rizes-olive focus:outline-none sm:text-sm sm:leading-6 transition-colors motion-reduce:transition-none [appearance:textfield]"
                          id="children"
                          type="number"
                          min={0}
                          max={maxGuests}
                          value={children}
                          onChange={(e) => setChildren(Math.max(0, parseInt(e.target.value) || 0))}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Guest count warning */}
                {tooManyGuests && (
                  <div className="mt-4 p-3 rounded-lg bg-red-50 text-red-700 ring-1 ring-red-200 text-sm flex items-center justify-between">
                    <span>Maximum {maxGuests} guests allowed</span>
                    <span className="font-medium">{adults + children} selected</span>
                  </div>
                )}

                {/* Price Preview */}
                {nights > 0 && !tooFewNights && property && (() => {
                  const breakdown = calculatePrice(property, checkin, checkout);
                  return (
                    <div className="mt-6 px-4 py-3 bg-rizes-ivory rounded-xl flex items-center justify-between ring-1 ring-rizes-limestone/50">
                      <span className="text-sm text-rizes-olive">
                        {breakdown.nights} night{breakdown.nights !== 1 ? 's' : ''}
                      </span>
                      <span className="text-right">
                        <span className="text-xs text-gray-500 block">Estimated total</span>
                        <span className="font-bold text-lg text-rizes-ink">{formatPrice(breakdown.total)}</span>
                      </span>
                    </div>
                  );
                })()}

                {/* Error display */}
                {error && (
                  <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                {/* Submit Button */}
                <div className="mt-8">
                  <button
                    className="w-full flex justify-center items-center gap-2 rounded-lg bg-rizes-olive px-6 py-4 text-base font-medium text-white hover:bg-rizes-olive-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rizes-olive focus-visible:ring-offset-2 transition-colors disabled:opacity-70 disabled:cursor-not-allowed motion-reduce:transition-none"
                    type="submit"
                    disabled={isSubmitting || tooFewNights || tooManyGuests}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin motion-reduce:animate-none h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      'Book Your Stay'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingEngine;
